import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Column, CellProps } from 'react-table';
import { FaDownload } from 'react-icons/fa';
import styled from 'styled-components';

import MainLayout from 'components/main-layout/MainLayout';
import LayoutContent from 'components/main-layout/LayoutContent';
import ReactTableWrapper from 'components/table/ReactTableWrapper';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import { useModal } from 'store/modal';
import { formatDate } from 'utils/helpers/formatters';
import updateArchiveCount from 'utils/archiveCount';
import { IArchive } from 'types/archives';
import { getArchives, getArchiveDownloadLink, formatArchiveType, formatArchiveStatus } from 'utils/helpers/archives';

const ArchivesPage: FC = () => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery('archives', getArchives);

  const handleDownload = async (archiveId: number) => {
    try {
      setDownloadingId(archiveId);
      const downloadLink = await getArchiveDownloadLink(archiveId);

      const anchor = document.createElement('a');
      anchor.href = downloadLink;
      anchor.target = '_blank';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Update archive count immediately after download
      await updateArchiveCount();
    } catch (error) {
      showModal({
        type: 'error',
        content: t('archives.downloadError'),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const columns: Array<Column<IArchive>> = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id',
      },
      {
        Header: t('archives.fileName') || 'File Name',
        accessor: 'file_name',
        width: '32%',
        Cell: ({ row }: CellProps<IArchive>) => {
          const isExpired = row.original.status === 'expired';
          const isReady = row.original.status === 'ready-for-download';
          const fileName = row.original.file_name || `archive_${row.original.id}`;

          if (isReady) {
            return (
              <Button
                btnType="link"
                size="sm"
                disabled={downloadingId === row.original.id}
                onClick={() => handleDownload(row.original.id)}
              >
                {downloadingId === row.original.id ? (
                  <>
                    <Spinner size={14} />
                    <span className="ml-2">{t('archives.downloading')}</span>
                  </>
                ) : (
                  <>
                    <FaDownload
                      size={14}
                      className="mr-2"
                    />
                    {fileName}
                  </>
                )}
              </Button>
            );
          }

          return <span style={{ color: isExpired ? '#dc3545' : 'inherit' }}>{fileName}</span>;
        },
      },
      {
        Header: t('archives.status') || '',
        accessor: 'status',
        width: '20%',
        Cell: ({ row }: CellProps<IArchive>) => {
          const { text } = formatArchiveStatus(row.original, t);
          const isExpired = row.original.status === 'expired';

          return <span style={{ color: isExpired ? '#dc3545' : 'inherit' }}>{text}</span>;
        },
      },
      {
        Header: t('archives.createdAt') || '',
        accessor: 'created_at',
        width: '20%',
        Cell: ({ value }: CellProps<IArchive>) => {
          const date = new Date(value);
          return <span>{formatDate(date, 'dd.mm.YYY hh:ii')}</span>;
        },
      },
      {
        Header: t('archives.type') || '',
        accessor: 'archive_type',
        width: '25%',
        Cell: ({ row }: CellProps<IArchive>) => {
          return <span>{formatArchiveType(row.original.archive_type, t)}</span>;
        },
      },
    ],
    [t, downloadingId]
  );

  return (
    <MainLayout title={t('archives.title')}>
      <LayoutContent isLoading={isLoading}>
        <ContentWrapper>
          {data && data.items && data.items.length > 0 ? (
            <ReactTableWrapper<IArchive>
              columns={columns}
              data={data.items}
              loading={isLoading}
              hideFilterBar
              hideSearch
              disablePagination
              showID
            />
          ) : (
            <EmptyState>
              <EmptyMessage>{t('archives.noArchives')}</EmptyMessage>
            </EmptyState>
          )}
        </ContentWrapper>
      </LayoutContent>
    </MainLayout>
  );
};

export default ArchivesPage;

const ContentWrapper = styled.div`
  padding: 20px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const EmptyMessage = styled.p`
  font-size: 16px;
  color: #999;
`;
